import React, { FC, ReactNode, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { IVideo, formatVideoName } from '../../pages/admin/videos/hooks/useFetchVideos';
import { fancyTimeFormat } from '../../pages/admin/videos/adminVideosV2/helper';
import { DownloadBtn } from '../../pages/admin/videos/selectedVideoCardV2/DownloadBtn';
import { cldUrl } from '../../utils/cloudinary';
import { isInternal } from '../../utils';
import { useUser } from '../../hooks/useUser';
import { getVideoStatus, isDemo, isNonProject, getVideoFilename } from './videoStatus';
import { StatusPill, FlagChip } from './StatusPill';
import { VideoCardMenu, VideoMenuContext } from './VideoCardMenu';
import { PdpCountPill, PlacedPill } from '../PdpCountPill/PdpCountPill';
import { groupVideoOptions, getVideoChannelLabel, getVideoContentTypeLabel, formatFps, formatVideoFileSize } from './videoDetails';
import { FileSizeSpec } from './FileSizeSpec';
import { usePortalPath } from '../../hooks/usePortalPath'
import { PLAY_GLYPH, thumbSource } from './videoThumb';
import { RestoreVideoButton } from './RestoreVideoButton';

export type VideoCardVariant = 'browse' | 'row' | 'compact' | 'share';

export interface VideoCardProps {
  video: IVideo;
  variant: VideoCardVariant;
  onOpen: (video: IVideo) => void;
  refetch?: () => void;
  project?: any;
  /** Expert page passes false — the row is redundant there (§07). */
  showExpert?: boolean;
  /** Anonymous/public surface. Independent of `variant` — share pages use the
   *  `row` variant in list view, so share-ness must not key off the variant. */
  isShare?: boolean;
  /** Share download gating (shortlist download_enabled etc.). */
  hideDownload?: boolean;
  ignoreFileTypeSettings?: boolean;
  showMenu?: boolean;
  renderInternalStatus?: (video: IVideo) => ReactNode;
  onRemoveFromVideoShortlist?: () => void;
  menuContext?: VideoMenuContext;
  /** Upload affordance (§10) — rendered only when the surface passes it. */
  uploadSlot?: ReactNode;
  /** Admin "Deleted" view: a soft-deleted video. The card is read-only apart
   *  from Restore — no detail view, download, review, menu or upload. */
  deleted?: boolean;
}

export const TechChip: FC<{ children: ReactNode }> = ({ children }) => (
  <span
    className="inline-flex items-center h-[21px] px-2 rounded-md bg-scrim font-bold tabular-nums"
    style={{ fontSize: 11, color: '#EAEDF2' }}
  >
    {children}
  </span>
);

/** §01 — the card. Four named variants replace VideoCardV2's boolean props. */
export const VideoCard: FC<VideoCardProps> = (props) => {
  const {
    video,
    variant,
    onOpen,
    refetch = () => {},
    project,
    showExpert = true,
    isShare,
    hideDownload = false,
    ignoreFileTypeSettings = false,
    showMenu = true,
    renderInternalStatus,
    onRemoveFromVideoShortlist,
    menuContext,
    uploadSlot,
    deleted,
  } = props;
  const navigate = useNavigate();
  const { p } = usePortalPath();
  const { role } = useUser();

  const st = getVideoStatus(video, project);
  const title = formatVideoName(video?.title || video?.name || video?.key || '') || 'Untitled video';
  const filename = getVideoFilename(video);
  const isRow = variant === 'row';
  const compact = variant === 'compact';
  const share = isShare ?? variant === 'share';
  const duration = video?.length ? fancyTimeFormat(video.length) : '';
  const pdpCount = Number(video?.pdp_url_count) || 0;
  const placedCount = Number(video?.placed_count) || 0;
  const source = thumbSource(video);
  // The list said so, or the row does (`deactivated` when the API selects it).
  const isDeleted = !share && (!!deleted || (video as any)?.deactivated === true);
  // The detail view reads the live file, which a deleted one isn't.
  const handleOpen = isDeleted ? undefined : () => onOpen(video);

  const product = useMemo(() => {
    if (video.videoProducts?.[0]) return video.videoProducts[0];
    if (project?.products?.[0]) return project.products[0];
    if (project?.product) return project.product;
    return null;
  }, [video, project]);
  const expert = showExpert ? video.videoPros?.[0] : undefined;
  // Row lists ALL experts (37 live videos carry 2-4); stack variants keep
  // the design's single-expert line.
  const rowExperts = showExpert ? video.videoPros || [] : [];

  const optionGroups = useMemo(() => groupVideoOptions(video), [video]);
  // Project context: content type and channel ("PDP #2") from the row's own
  // trimmed project, or from the `project` prop on the Downloads tab.
  const contentTypeLabel = getVideoContentTypeLabel(video, project);
  const channelLabel = getVideoChannelLabel(video, project);

  const goToExpert = (event: React.MouseEvent, person: any) => {
    event.stopPropagation();
    if (person?.userid) navigate(p(`/experts/${person.userid}/${person.displayName}?proId=${person.userid}`));
  };

  const goToProduct = (event: React.MouseEvent) => {
    event.stopPropagation();
    const desireid = video.videoProducts?.[0]?.desireid;
    if (!desireid) return;
    const brandId = project?.brand_id ?? (video as any).project?.brand_id;
    if (isInternal(role) && brandId) navigate(p(`/products/${brandId}?productId=${desireid}`));
    else navigate(p(`/products?productId=${desireid}`));
  };

  const flags = (
    <>
      <StatusPill status={st.key} label={st.label} />
      {isDeleted && <FlagChip label="Deleted" />}
      {/* Internal-only: brand, expert and share viewers never see the chip. */}
      {isInternal(role) && isDemo(video) && <FlagChip gold label="Demo" />}
      {!share && isNonProject(video) && <FlagChip label="Non-project" />}
      {/* Same pills as the product cards: the PDP URLs this video is on,
          and "Placed" when the last analysis found it on one of them. Both
          render nothing at zero. */}
      <PdpCountPill count={pdpCount} />
      <PlacedPill count={placedCount} />
    </>
  );

  const actions = isDeleted ? (
    <div
      className={classNames('flex items-center gap-1.5 shrink-0', { 'ml-auto': !isRow })}
      onClick={(e) => e.stopPropagation()}
    >
      <RestoreVideoButton video={video} title={title} onRestored={refetch} />
    </div>
  ) : (
    <div
      className={classNames('flex items-center gap-1.5 shrink-0', { 'ml-auto': !isRow })}
      onClick={(e) => e.stopPropagation()}
    >
      {uploadSlot}
      {/* internal-status lives in the body for stack variants (see below) and in
          the row header for `row` — never in both. */}
      {!hideDownload && st.hasFile && (
        <DownloadBtn
          video={video}
          variant="card"
          project={project}
          ignoreFileTypeSettings={ignoreFileTypeSettings}
        />
      )}
      {showMenu && (
        // §09.3 — below md the row drops its kebab; the card taps into the
        // detail view, where the actions live with room for labels.
        <span className={`items-center gap-1.5 ${isRow ? 'hidden md:inline-flex' : 'inline-flex'}`}>
          <VideoCardMenu
            video={video}
            refetch={refetch}
            project={project}
            context={share ? menuContext : { reviewButton: isRow ? 'compact' : 'icon', ...menuContext }}
            onRemoveFromVideoShortlist={onRemoveFromVideoShortlist}
            share={share}
            title={title}
          />
        </span>
      )}
    </div>
  );

  const productRow = (
    <span
      className={classNames('inline-flex items-center gap-2 min-w-0 text-text-1', {
        'cursor-pointer': !!video.videoProducts?.[0]?.desireid,
      })}
      style={{ fontSize: 12.5 }}
      onClick={goToProduct}
      role="link"
      tabIndex={-1}
    >
      <span className="w-6 h-6 shrink-0 rounded-md bg-surface-2 text-accent-text inline-flex items-center justify-center overflow-hidden">
        {product?.image ? (
          <img src={product.image} alt="" loading="lazy" className="w-6 h-6 object-cover" />
        ) : (
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 8 12 3 3 8v8l9 5 9-5z" />
            <path d="m3 8 9 5 9-5M12 13v8" />
          </svg>
        )}
      </span>
      <span className={classNames('truncate', { 'hover:text-accent-text hover:underline': !!video.videoProducts?.[0]?.desireid, 'text-text-2': !product })}>
        {product?.title || 'No product linked'}
      </span>
    </span>
  );

  const renderExpert = (person: any) => (
    <span
      key={person.userid || person.displayName}
      className="inline-flex items-center gap-2 min-w-0 text-text-1 cursor-pointer"
      style={{ fontSize: 12.5 }}
      onClick={(e) => goToExpert(e, person)}
      role="link"
      tabIndex={-1}
    >
      <span className="w-6 h-6 shrink-0 rounded-full bg-tint text-accent-text inline-flex items-center justify-center overflow-hidden font-bold" style={{ fontSize: 10 }}>
        {person.avatar ? (
          <img
            src={cldUrl(person.avatar, { w: 48, h: 48, crop: 'fill', gravity: 'face' })}
            alt=""
            loading="lazy"
            className="w-6 h-6 object-cover"
          />
        ) : (
          (person.displayName || '')
            .split(' ')
            .map((w: string) => w[0])
            .join('')
            .slice(0, 2)
            .toUpperCase()
        )}
      </span>
      <span className="truncate hover:text-accent-text hover:underline">{person.displayName}</span>
    </span>
  );
  const expertRow = expert ? renderExpert(expert) : null;

  // One wrapping chip row: content type and channel on the tinted plate,
  // then the project's additional options (watermark variants, caption
  // languages) on the neutral one — most videos carry none of the latter.
  // Compact strips (expert / product hosts) stay dense and skip the row.
  const contextChips = compact
    ? null
    : [
        contentTypeLabel && { key: 'content-type', label: contentTypeLabel },
        channelLabel && { key: 'channel', label: channelLabel },
      ].filter(Boolean) as Array<{ key: string; label: string }>;
  const hasContextChips = !!contextChips && contextChips.length > 0;
  const optionChips = (hasContextChips || optionGroups.length > 0) && (
    <div className="flex flex-wrap gap-1.5">
      {(contextChips || []).map((chip) => (
        <span
          key={chip.key}
          className="inline-flex items-center h-[22px] px-[9px] rounded-full bg-tint text-accent-text"
          style={{ fontSize: 11 }}
        >
          {chip.label}
        </span>
      ))}
      {optionGroups.map((g) => (
        <span
          key={g.optionId}
          className="inline-flex items-center h-[22px] px-[9px] rounded-full bg-surface-2 text-text-1"
          style={{ fontSize: 11 }}
        >
          {g.optionName}: {g.displayValue}
        </span>
      ))}
    </div>
  );

  // ---------------- row variant ----------------
  if (isRow) {
    // Share surfaces never expose the internal production filename.
    const rowFilename = share ? '' : filename;
    // ALL technical metadata rides the title line: version/ratio, filename,
    // duration. Line 2 is purely relational (product, experts). PDP /
    // JSON-LD presence sits with the flags as pills.
    // The filename is the only meta piece that truncates — the title yields
    // first, then the filename; the numeric specs are `shrink-0` and stay
    // readable at every width.
    const titleSpec = [video?.version ? `v${video.version}` : '', video?.aspect_ratio || '', formatFps(video?.fps)]
      .filter(Boolean)
      .join('  ·  ');
    const tailSpec = duration;
    return (
      <div
        data-file-id={video?.id}
        className={classNames(
          'flex items-start gap-3.5 w-full box-border p-3 bg-surface border border-border-token rounded-xl transition-colors duration-150',
          { 'cursor-pointer hover:border-accent': !isDeleted }
        )}
        onClick={handleOpen}
        title={title}
      >
        <div
          className="relative w-24 h-[54px] shrink-0 rounded-lg bg-track text-muted flex items-center justify-center overflow-hidden"
          style={isDeleted ? { opacity: 0.55 } : undefined}
        >
          {PLAY_GLYPH(20)}
          <img
            src={source}
            alt=""
            loading="lazy"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }}
          />
        </div>
        {/* Two lines, split by information TYPE: everything ABOUT the file
            (status, title, version/ratio, filename, duration) on line 1;
            line 2 is purely relational (product, experts). Within a group the
            separator is a middot; between groups a wider gap — whitespace is
            the grammar. Groups wrap as units on narrow widths. */}
        <div className="flex-1 min-w-0 flex flex-col gap-2">
          <div className="flex items-start gap-2.5">
            <div className="flex-1 min-w-0 flex items-center flex-wrap gap-y-1.5 gap-x-2.5">
              {flags}
              <span className="font-bold text-text-1 truncate min-w-0" style={{ fontSize: 14.5, lineHeight: 1.3 }}>
                {title}
              </span>
              {(titleSpec || formatVideoFileSize(video?.file_size) || rowFilename || tailSpec) && (
                <span className="flex items-center gap-2 min-w-0 text-xs text-text-2">
                  {titleSpec && <span className="shrink-0 tabular-nums">{titleSpec}</span>}
                  {titleSpec && formatVideoFileSize(video?.file_size) ? <span className="shrink-0">·</span> : null}
                  {formatVideoFileSize(video?.file_size) ? (
                    <span className="shrink-0">
                      <FileSizeSpec size={video?.file_size} />
                    </span>
                  ) : null}
                  {(titleSpec || formatVideoFileSize(video?.file_size)) && (rowFilename || tailSpec) ? <span className="shrink-0">·</span> : null}
                  {rowFilename && <span className="truncate min-w-0">{rowFilename}</span>}
                  {rowFilename && tailSpec ? <span className="shrink-0">·</span> : null}
                  {tailSpec && <span className="shrink-0 tabular-nums">{tailSpec}</span>}
                </span>
              )}
            </div>
            {renderInternalStatus && (
              <span onClick={(e) => e.stopPropagation()} className="hidden md:flex items-center gap-1.5 shrink-0">
                {renderInternalStatus(video)}
              </span>
            )}
            {actions}
          </div>
          <div className="flex items-center flex-wrap gap-y-2 gap-x-5 min-w-0">
            {productRow}
            {rowExperts.map(renderExpert)}
          </div>
          {optionChips}
        </div>
      </div>
    );
  }

  // ---------------- stack variants: browse / compact / share ----------------
  const thumbH = compact ? 108 : 158;
  const showFilename = !compact && !share && !!filename;
  return (
    <div
      // Lets tests (and support) find one deliverable's card on any page.
      data-file-id={video?.id}
      className={classNames(
        'bg-surface border border-border-token rounded-xl overflow-hidden flex flex-col transition-colors duration-150 box-border',
        { 'cursor-pointer hover:border-accent': !isDeleted },
        // Fill the grid/flex cell so every card in a row matches the tallest.
        compact ? 'shrink-0' : 'h-full'
      )}
      style={{ boxShadow: 'var(--sh-1)', width: '100%' }}
      onClick={handleOpen}
      title={title}
    >
      <div
        className="relative shrink-0 bg-track text-muted flex items-center justify-center"
        style={{ height: thumbH, opacity: isDeleted ? 0.55 : undefined }}
      >
        {PLAY_GLYPH(compact ? 26 : 30)}
        <img
          src={source}
          alt=""
          loading="lazy"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = 'none';
          }}
        />
        <div className="absolute left-2 right-2 bottom-2 flex flex-wrap justify-end gap-[5px]">
          {video?.version ? <TechChip>v{video.version}</TechChip> : null}
          {video?.aspect_ratio ? <TechChip>{video.aspect_ratio}</TechChip> : null}
          {formatFps(video?.fps) ? <TechChip>{formatFps(video?.fps)}</TechChip> : null}
          {formatVideoFileSize(video?.file_size) ? (
            <TechChip>
              <FileSizeSpec size={video?.file_size} />
            </TechChip>
          ) : null}
          {duration ? <TechChip>{duration}</TechChip> : null}
        </div>
      </div>
      <div
        className={classNames('flex flex-col', { 'flex-1 min-h-0': !compact })}
        style={{ gap: compact ? 9 : 12, padding: compact ? '12px 13px 13px' : '13px 15px 15px' }}
      >
        <div className="flex items-start flex-wrap gap-y-2 gap-x-2.5">
          <div className="min-w-0 flex items-center flex-wrap gap-[7px]">{flags}</div>
          {actions}
        </div>
        <div>
          <div
            className="font-bold text-text-1"
            style={{
              fontSize: compact ? 13.5 : 15.5,
              lineHeight: 1.3,
              display: '-webkit-box',
              WebkitLineClamp: compact ? 1 : 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {title}
          </div>
          {showFilename && (
            <div className="text-text-2 truncate tabular-nums" style={{ marginTop: 5, fontSize: 11.5 }}>
              {filename}
            </div>
          )}
        </div>
        {renderInternalStatus && <span onClick={(e) => e.stopPropagation()}>{renderInternalStatus(video)}</span>}
        {(product || expert || !compact) && (
          <div
            className={classNames(
              'flex border-t border-border-token',
              compact ? 'flex-row items-center' : 'flex-col',
              { 'mt-auto': !compact }
            )}
            style={{ gap: compact ? 12 : 9, paddingTop: 11 }}
          >
            {productRow}
            {!compact && expertRow}
          </div>
        )}
        {optionChips}
      </div>
    </div>
  );
};

export default VideoCard;
